<template>
  <a-tabs v-model:activeKey="activeKey" @tabClick="tabClick">
    <a-tab-pane
      v-for="(item, index) in configList"
      :key="item.type"
      :tab="item.appName"
      :disabled="loading"
    >
    </a-tab-pane>
  </a-tabs>
  <div class="h5-jump-config">
    <div class="config-container">
      <!-- 加载状态 -->
      <a-spin :spinning="loading" tip="加载中...">
        <!-- 跳转配置区域 -->
        <div class="jump-section">
          <div class="section-title">
            <span class="label">统一跳转</span>
          </div>

          <a-radio-group
            v-model:value="selectedJumpType"
            @change="handleJumpTypeChange"
          >
            <a-radio
              v-for="option in jumpOptions"
              :key="option.value"
              :value="option.value"
              class="radio-item"
            >
              {{ option.label }}
            </a-radio>
          </a-radio-group>
          <a-alert
            message="选择默认表示不统一配置，遵循App内的配置，自定义则根据规则来跳转"
            type="info"
            show-icon
          />
        </div>

        <!-- 自定义配置区域 -->
        <div class="custom-section" v-if="selectedJumpType === '3'">
          <div class="section-title">
            <span class="label custom-label">自定义</span>
            <a-button
              type="primary"
              shape="circle"
              size="small"
              class="add-btn"
              @click="addCtrConfig"
            >
              <template #icon>
                <PlusOutlined />
              </template>
            </a-button>
          </div>

          <!-- 暂无数据状态 -->
          <a-empty 
            v-if="ctrConfigs.length === 0" 
            description="暂无配置数据"
            :image="Empty.PRESENTED_IMAGE_SIMPLE"
          >
            <a-button type="primary" @click="addCtrConfig">
              添加配置
            </a-button>
          </a-empty>

          <!-- CTR配置列表 -->
          <div
            class="ctr-config"
            v-for="(config, index) in ctrConfigs"
            :key="index"
            v-else
          >
            <!-- <div class="ctr-label">CTR大于N小于N</div>    -->
            <a-select
              v-model:value="config.jump"
              class="ctr-dropdown"
              placeholder="请选择类型"
            >
              <a-select-option :value="0">默认</a-select-option>
              <a-select-option :value="1">外部</a-select-option>
              <a-select-option :value="2">内部</a-select-option>
            </a-select>

            <!-- CTR范围值输入 -->
            <div class="ctr-range-container">
              <div class="range-label">CTR范围值:</div>
              <div class="range-inputs">
                <a-input-number
                  v-model:value="config.ctrRange.min"
                  class="range-input"
                  placeholder="最小值"
                  :min="0"
                />
                <span class="range-separator">-</span>
                <a-input-number
                  v-model:value="config.ctrRange.max"
                  class="range-input"
                  placeholder="最大值"
                  :min="0"
                />
              </div>
            </div>

            <!-- 减号按钮 -->
            <a-button
              v-if="ctrConfigs.length > 1"
              type="primary"
              danger
              shape="circle"
              size="small"
              class="remove-btn"
              @click="removeCtrConfig(index)"
            >
              <template #icon>
                <MinusOutlined />
              </template>
            </a-button>
          </div>
        </div>

      </a-spin>
      
      <!-- 保存按钮区域 -->
      <div class="save-section">
        <a-button 
          type="primary" 
          size="large" 
          :loading="saving"
          @click="handleSave"
        >
          <template #icon>
            <SaveOutlined />
          </template>
          保存配置
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from "vue";
import { useRouter } from "vue-router";
import { PlusOutlined, MinusOutlined, SaveOutlined } from "@ant-design/icons-vue";
import { Empty } from "ant-design-vue";
import {
  jumpConfig,
  jumpConfigSave,
  jumpConfigCtr,
  jumpConfigCtrSave,
} from "@/api/api";
import { message } from "ant-design-vue";

const router = useRouter();

const configList = ref([
  { type: 0, appName: "火山开发者" },
  { type: 1, appName: "玫瑰开发者" },
]);
const activeKey = ref(0);

// 跳转选项配置
const jumpOptions = reactive([
  { label: "默认", value: "0" },
  { label: "外部", value: "1" },
  { label: "内部", value: "2" },
  { label: "自定义", value: "3" },
]);

// 响应式数据
const selectedJumpType = ref("0");
const selectedCtrType = ref("default");
const loading = ref(false);
const saving = ref(false);
const jumpConfigId = ref(0);

// CTR配置数组
const ctrConfigs = ref([
  {
    jump: 0,
    ctrRange: {
      min: 0,
      max: 100,
    },
  },
]);

// 添加CTR配置
const addCtrConfig = () => {
  ctrConfigs.value.push({
    jump: 0,
    ctrRange: {
      min: 0,
      max: 100,
    },
  });
};

// 保存CTR配置
const saveCtrConfigs = () => {
  let list = handleCtrConfigs();
  return saveCtrConfigRequest(list);
};

// 保存CTR配置请求
const saveCtrConfigRequest = (list: any[]) => {
  return jumpConfigCtrSave({
    type: activeKey.value,
    list: list,
  });
};

const handleCtrConfigs = () => {
  let list: any[] = [];
  ctrConfigs.value.forEach((item: any) => {
    var params: any = {
      ctrRange: `${item.ctrRange.min},${item.ctrRange.max}`,
      jump: item.jump,
      type: activeKey.value,
    };
    if (item.id >= 0) {
      params.id = item.id;
    }
    list.push(params);
  });
  return list;
};

// 删除CTR配置
const removeCtrConfig = (index: number) => {
  if (ctrConfigs.value.length > 1) {
    ctrConfigs.value.splice(index, 1);
  }
};

// 获取CTR范围值数据格式
const getCtrRangeData = () => {
  return ctrConfigs.value.map((config) => {
    const rangeStr =
      config.ctrRange.min !== null && config.ctrRange.max !== null
        ? `${config.ctrRange.min},${config.ctrRange.max}`
        : "";
    return {
      ctrRange: rangeStr,
      jump: config.jump,
    };
  });
};

// 处理跳转类型变化
const handleJumpTypeChange = (e: any) => {
  //console.log("选择的跳转类型:", selectedJumpType.value);
  if (selectedJumpType.value === "3") {
    //console.log("启用自定义配置");
  }
};

// 保存所有配置
const handleSave = async () => {
  saving.value = true;
  
  try {
    // 保存跳转配置
    let jumpParams: any = {
      type: activeKey.value,
      jump: Number(selectedJumpType.value),
    };
    if (jumpConfigId.value > 0) {
      jumpParams.id = jumpConfigId.value;
    }
    
    await jumpConfigSave(jumpParams);
    
    // 如果是自定义配置，还需要保存CTR配置
    if (selectedJumpType.value === "3") {
      await saveCtrConfigs();
    }
    
    message.success("配置保存成功");
  } catch (error) {
    console.error("保存失败:", error);
    message.error("保存失败，请重试");
  } finally {
    saving.value = false;
  }
};

const tabClick = (key: string) => {
  activeKey.value = Number(key);
  ctrConfigs.value = [
    {
      jump: 0,
      ctrRange: {
        min: 0,
        max: 100,
      },
    },
  ];
  jumpConfigId.value = 0;
  selectedJumpType.value = "0";
  getJumpConfig();
};

// 处理CTR类型变化
const handleCtrTypeChange = () => {
  //console.log("选择的CTR类型:", selectedCtrType.value);
};

// 返回上一页
const goBack = () => {
  router.go(-1);
};

const getCtrConfig = () => {
  loading.value = true;
  jumpConfigCtr({
    type: activeKey.value,
  }).then((res) => {
    //console.log(res);
    if (res.data && res.data.length) {
      ctrConfigs.value = res.data;
      ctrConfigs.value.forEach((item: any) => {
        item.ctrRange = item.ctrRange.split(",");
        item.ctrRange.min = Number(item.ctrRange[0]);
        item.ctrRange.max = Number(item.ctrRange[1]);
      });
    } else {
      // 如果没有数据，清空配置数组，显示空状态
      ctrConfigs.value = [];
    }
  }).catch((error) => {
    console.error("获取CTR配置失败:", error);
    message.error("获取配置失败，请重试");
    ctrConfigs.value = [];
  }).finally(() => {
    loading.value = false;
  });
};

const getJumpConfig = () => {
  loading.value = true;
  jumpConfig({
    type: activeKey.value,
  }).then((res) => {
    //console.log(res);
    if (res.data) {
      selectedJumpType.value = res.data.jump.toString();
      jumpConfigId.value = res.data.id || 0;
      if (selectedJumpType.value === "3") {
        getCtrConfig();
      }
    } else {
      // 如果没有数据，使用默认值
      selectedJumpType.value = "0";
      jumpConfigId.value = 0;
    }
  }).catch((error) => {
    console.error("获取跳转配置失败:", error);
    message.error("获取配置失败，请重试");
    selectedJumpType.value = "0";
    jumpConfigId.value = 0;
  }).finally(() => {
    loading.value = false;
  });
};

onMounted(() => {
  getJumpConfig();
});
</script>

<style scoped lang="less">
.h5-jump-config {
  padding: 20px;
  min-height: 100vh;
}

.config-container {
  max-width: 800px;
  margin: 0;
  background: white;
  padding: 30px;
}

.section-title {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 15px;

  .label {
    font-size: 16px;
    font-weight: 600;
    color: #333;

    &.custom-label {
      color: #1890ff;
      font-weight: 700;
    }
  }

  .add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.jump-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.radio-item {
  margin-right: 20px;
  margin-bottom: 10px;
}

.custom-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.ctr-config {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 15px;
  padding: 15px;
  background-color: white;
  border-radius: 4px;
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }
}

.ctr-label {
  font-size: 14px;
  color: #1890ff;
  font-weight: 600;
  white-space: nowrap;
}

.ctr-dropdown {
  min-width: 120px;
}

.ctr-range-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.range-label {
  font-size: 14px;
  color: #1890ff;
  font-weight: 600;
  white-space: nowrap;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-input {
  width: 100px;
}

.range-separator {
  color: #666;
  font-weight: 600;
}

.remove-btn {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.save-section {
  margin-top: 30px;
  padding-top: 20px;
//   border-top: 1px solid #eee;
}

.back-button-container {
  display: flex;
  justify-content: flex-start;
  margin-top: 30px;
}

.back-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background-color: #6c757d;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #5a6268;
  }

  .back-icon {
    font-size: 18px;
    font-weight: bold;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .config-container {
    padding: 20px;
    margin: 10px;
  }

  .radio-item {
    display: block;
    margin-bottom: 10px;
  }

  .ctr-config {
    flex-direction: column;
    align-items: flex-start;
  }

  .ctr-dropdown {
    width: 100%;
    min-width: auto;
  }

  .range-inputs {
    width: 100%;
    justify-content: center;
  }

  .remove-btn {
    margin-left: 0;
    align-self: flex-end;
  }
}
</style>
